import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Flip } from "gsap/Flip";
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEventHandler,
  type MouseEventHandler,
} from "react";
import { Button } from "react-aria-components";

import arrowRightIcon from "@/assets/figma/chat-add-menu/arrow-right.svg";
import closeIcon from "@/assets/figma/chat-extensions/close.svg";
import moreIcon from "@/assets/figma/chat-extensions/more.svg";
import { cn } from "@/shared/lib/cn";
import type { MerryChatExtensionViewModel } from "@/shared/ui/chatExtensionTypes";

gsap.registerPlugin(Flip, useGSAP);

const EXTENSION_GAP = 6;
const MORE_BUTTON_WIDTH = 34;

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

interface MerryChatExtensionChipProps {
  extension: MerryChatExtensionViewModel;
  onConfigure?: (extensionId: string) => void;
  onRemove: (extensionId: string) => void;
}

export function MerryChatExtensionChip({
  extension,
  onConfigure,
  onRemove,
}: MerryChatExtensionChipProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);

  useGSAP(
    () => {
      const root = rootRef.current;
      if (!root) return;

      const sourceIcon = root.querySelector<HTMLElement>(
        ".merry-chat-extension-chip__source-icon",
      );
      const removeIcon = root.querySelector<HTMLElement>(
        ".merry-chat-extension-chip__remove-icon",
      );
      if (!sourceIcon || !removeIcon) return;

      gsap.killTweensOf([sourceIcon, removeIcon]);
      if (prefersReducedMotion()) {
        gsap.set(sourceIcon, { autoAlpha: isActive ? 0 : 1, rotation: 0 });
        gsap.set(removeIcon, { autoAlpha: isActive ? 1 : 0, rotation: 0 });
        return;
      }

      gsap.to(sourceIcon, {
        autoAlpha: isActive ? 0 : 1,
        duration: 0.14,
        ease: "power2.out",
        overwrite: true,
        rotation: isActive ? -18 : 0,
      });
      gsap.to(removeIcon, {
        autoAlpha: isActive ? 1 : 0,
        duration: 0.14,
        ease: "power2.out",
        overwrite: true,
        rotation: isActive ? 0 : 18,
      });
    },
    { dependencies: [isActive], scope: rootRef },
  );

  useGSAP(
    () => {
      const summary = rootRef.current?.querySelector<HTMLElement>(
        ".merry-chat-extension-chip__summary",
      );
      if (!summary || prefersReducedMotion()) return;

      gsap.fromTo(
        summary,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: 0.18,
          ease: "power2.out",
          overwrite: true,
          onComplete: () =>
            gsap.set(summary, { clearProps: "opacity,visibility" }),
        },
      );
    },
    { dependencies: [extension.summary], scope: rootRef },
  );

  const activate: MouseEventHandler<HTMLDivElement> = () => setIsActive(true);
  const deactivate: MouseEventHandler<HTMLDivElement> = () =>
    setIsActive(false);
  const handleFocus: FocusEventHandler<HTMLDivElement> = () => setIsActive(true);
  const handleBlur: FocusEventHandler<HTMLDivElement> = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setIsActive(false);
  };

  const copy = (
    <span className="merry-chat-extension-chip__copy">
      <span>{extension.label}</span>
      {extension.summary ? (
        <>
          <span aria-hidden="true">·</span>
          <span className="merry-chat-extension-chip__summary">
            {extension.summary}
          </span>
        </>
      ) : null}
    </span>
  );

  return (
    <div
      className="merry-chat-extension-chip"
      data-chat-extension-configurable={extension.configurable}
      data-chat-extension-id={extension.id}
      data-chat-extension-kind={extension.kind}
      data-hovered={isActive || undefined}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onMouseEnter={activate}
      onMouseLeave={deactivate}
      ref={rootRef}
    >
      {extension.configurable ? (
        <Button
          aria-label={`配置${extension.label}`}
          className={({ isFocusVisible, isPressed }) =>
            cn(
              "merry-chat-extension-chip__main",
              isPressed && "merry-chat-extension-chip__main--pressed",
              isFocusVisible &&
                "merry-chat-extension-chip__main--focus-visible",
            )
          }
          onPress={() => onConfigure?.(extension.id)}
        >
          {copy}
          <span
            aria-hidden="true"
            className="merry-chat-extension-chip__arrow"
          >
            <img alt="" src={arrowRightIcon} />
          </span>
        </Button>
      ) : (
        <span className="merry-chat-extension-chip__main">{copy}</span>
      )}

      <span aria-hidden="true" className="merry-chat-extension-chip__icon-slot">
        <img
          alt=""
          className="merry-chat-extension-chip__source-icon"
          src={extension.icon}
        />
      </span>
      <Button
        aria-label={`移除${extension.label}`}
        className={({ isFocusVisible, isPressed }) =>
          cn(
            "merry-chat-extension-chip__remove",
            isPressed && "merry-chat-extension-chip__remove--pressed",
            isFocusVisible && "merry-chat-extension-chip__remove--focus-visible",
          )
        }
        onPress={() => onRemove(extension.id)}
      >
        <img
          alt=""
          aria-hidden="true"
          className="merry-chat-extension-chip__remove-icon"
          src={closeIcon}
        />
      </Button>
    </div>
  );
}

export interface MerryChatExtensionBarProps {
  className?: string;
  extensions: ReadonlyArray<MerryChatExtensionViewModel>;
  isMoreOpen?: boolean;
  onConfigure?: (extensionId: string) => void;
  onMore?: (
    hiddenExtensions: ReadonlyArray<MerryChatExtensionViewModel>,
  ) => void;
  onRemove: (extensionId: string) => void;
}

export function MerryChatExtensionBar({
  className,
  extensions,
  isMoreOpen,
  onConfigure,
  onMore,
  onRemove,
}: MerryChatExtensionBarProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRefs = useRef(new Map<string, HTMLSpanElement>());
  const previousFlipStateRef = useRef<ReturnType<typeof Flip.getState> | null>(
    null,
  );
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [visibleCount, setVisibleCount] = useState(extensions.length);

  const measure = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;

    const availableWidth = Math.floor(root.getBoundingClientRect().width);
    const widths = extensions.map(
      (extension) =>
        measureRefs.current.get(extension.id)?.getBoundingClientRect().width ??
        0,
    );
    const totalWidth = widths.reduce(
      (total, width, index) =>
        total + width + (index === 0 ? 0 : EXTENSION_GAP),
      0,
    );
    setNaturalWidth((current) =>
      current === Math.ceil(totalWidth) ? current : Math.ceil(totalWidth),
    );

    if (totalWidth <= availableWidth) {
      setVisibleCount((current) =>
        current === extensions.length ? current : extensions.length,
      );
      return;
    }

    const itemBudget = Math.max(
      0,
      availableWidth - MORE_BUTTON_WIDTH - EXTENSION_GAP,
    );
    let usedWidth = 0;
    let nextVisibleCount = 0;
    for (const width of widths) {
      const nextWidth = usedWidth + (nextVisibleCount === 0 ? 0 : EXTENSION_GAP) + width;
      if (nextWidth > itemBudget) break;
      usedWidth = nextWidth;
      nextVisibleCount += 1;
    }

    setVisibleCount((current) =>
      current === nextVisibleCount ? current : nextVisibleCount,
    );
  }, [extensions]);

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(measure);
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === "undefined") {
      return () => cancelAnimationFrame(frame);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(root);
    for (const element of measureRefs.current.values()) observer.observe(element);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [measure]);

  const visibleExtensions = useMemo(
    () => extensions.slice(0, visibleCount),
    [extensions, visibleCount],
  );
  const hiddenCount = Math.max(0, extensions.length - visibleCount);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll<HTMLElement>(
      "[data-chat-extension-layout-item]",
    );
    const previousState = previousFlipStateRef.current;

    if (previousState && !prefersReducedMotion()) {
      Flip.killFlipsOf(targets);
      Flip.from(previousState, {
        duration: 0.22,
        ease: "power3.out",
        nested: true,
        prune: true,
        scale: false,
      });
    }

    const latestExtensionId = visibleExtensions.at(-1)?.id;
    const latestElement = latestExtensionId
      ? root.querySelector<HTMLElement>(
          `[data-chat-extension-id="${CSS.escape(latestExtensionId)}"]`,
        )
      : null;
    if (latestElement && !prefersReducedMotion()) {
      gsap.fromTo(
        latestElement,
        { autoAlpha: 0, y: 4 },
        {
          autoAlpha: 1,
          duration: 0.2,
          ease: "power2.out",
          overwrite: true,
          y: 0,
          onComplete: () =>
            gsap.set(latestElement, {
              clearProps: "opacity,transform,visibility",
            }),
        },
      );
    }

    previousFlipStateRef.current = Flip.getState(targets);
  }, [hiddenCount, visibleExtensions]);

  const removeExtension = (extensionId: string) => {
    const root = rootRef.current;
    if (root && !prefersReducedMotion()) {
      previousFlipStateRef.current = Flip.getState(
        root.querySelectorAll<HTMLElement>(
          "[data-chat-extension-layout-item]",
        ),
      );
    }
    onRemove(extensionId);
  };

  return (
    <div
      aria-label="已添加扩展"
      className={cn("merry-chat-extension-bar", className)}
      data-chat-extension-count={extensions.length}
      data-chat-extension-hidden-count={hiddenCount}
      ref={rootRef}
      role="group"
      style={naturalWidth > 0 ? { width: naturalWidth } : undefined}
    >
      <div className="merry-chat-extension-bar__visible">
        {visibleExtensions.map((extension) => (
          <span data-chat-extension-layout-item="" key={extension.id}>
            <MerryChatExtensionChip
              extension={extension}
              onConfigure={onConfigure}
              onRemove={removeExtension}
            />
          </span>
        ))}
        {hiddenCount > 0 ? (
          <Button
            aria-expanded={isMoreOpen}
            aria-haspopup="dialog"
            aria-label={`查看其余 ${hiddenCount} 个扩展`}
            className={({ isFocusVisible, isHovered, isPressed }) =>
              cn(
                "merry-chat-extension-bar__more",
                isHovered && "merry-chat-extension-bar__more--hovered",
                isPressed && "merry-chat-extension-bar__more--pressed",
                isFocusVisible &&
                  "merry-chat-extension-bar__more--focus-visible",
              )
            }
            data-chat-extension-layout-item=""
            onPress={() => onMore?.(extensions.slice(visibleCount))}
          >
            <img alt="" aria-hidden="true" src={moreIcon} />
          </Button>
        ) : null}
      </div>

      <div aria-hidden="true" className="merry-chat-extension-bar__measure">
        {extensions.map((extension) => (
          <span
            className="merry-chat-extension-bar__measure-chip"
            key={extension.id}
            ref={(element) => {
              if (element) measureRefs.current.set(extension.id, element);
              else measureRefs.current.delete(extension.id);
            }}
          >
            <span className="merry-chat-extension-bar__measure-icon" />
            <span>{extension.label}</span>
            {extension.summary ? <span>·</span> : null}
            {extension.summary ? <span>{extension.summary}</span> : null}
            {extension.configurable ? (
              <span className="merry-chat-extension-bar__measure-arrow" />
            ) : null}
          </span>
        ))}
      </div>
    </div>
  );
}
